import {AutomationNode, NodeConnection, NodeType} from 'crqlar-react-common/dist/models/automation/automation';
import {getDefaultNodeConfig, getNodeDisplay, isKnownNodeType, isTriggerNode} from '../config/nodeCategories';
import {assertFieldValue, getNodeDefinition} from '../config/nodeDefinitions';
import {getNodeOutputs} from '../config/nodeOutputs';
import {remapAttachmentSourceNodeIds, teamEmailWithInvalidAttachments} from '../config/teamEmail';
import {continueIfFields, writeContinueIfGroups} from '../config/continueIf';
import {readConditionGroups} from '../config/ifConditions';
import {LAYOUT_ROW_SPACING} from '../utils/autoLayout';
import {findOpenPosition} from '../utils/nodePlacement';
import type {AgentDraft, AgentOperation} from './types';

const connectionKey = (edge: NodeConnection) => `${edge.sourceNodeId}|${edge.targetNodeId}|${edge.sourceHandle || ''}`;

const HISTORY_LABEL_LIMIT = 80;

/** e.g. `Agent: Added Wait · Connected A → B`. */
export const agentHistoryLabel = (summary: string[]): string => {
    const label = `Agent: ${summary.join(' · ')}`;
    return label.length > HISTORY_LABEL_LIMIT ? `${label.slice(0, HISTORY_LABEL_LIMIT - 1)}…` : label;
};

/**
 * Only keys nodeDefinitions.json lists for the type, each of its declared type; `null` (a removal) passes
 * through to mergeConfig. The Unlayer guards need the node, so they live here.
 */
const parseConfig = (operation: AgentOperation, node: AutomationNode): Record<string, unknown> => {
    const config = operation.config === undefined ? {} : operation.config;
    if (!config || Array.isArray(config) || typeof config !== 'object') throw new Error('A node configuration must be an object.');
    const fields = getNodeDefinition(node.type)?.fields || [];
    for (const [key, value] of Object.entries(config)) {
        const field = fields.find(entry => entry.key === key);
        if (!field) throw new Error(`The agent cannot edit “${key}” on ${node.label || node.type}.`);
        if (value !== null) assertFieldValue(field, value);
    }
    // A generated HTML edit must not leave a different saved visual-builder design behind it.
    if ((config.content !== undefined || (config.emailMode && config.emailMode !== node.config.emailMode)) && node.config.unlayerSourceData) {
        throw new Error('This email has a saved builder design. Edit its content in the builder; the agent can still change its subject or recipients.');
    }
    if ('placeholders' in config && node.config.unlayerSourceData) {
        const design = JSON.stringify(node.config.unlayerSourceData);
        const nextKeys = ((config.placeholders || []) as Array<{key: string}>).map(item => item.key);
        if ((node.config.placeholders || []).some((item: {key: string}) => !nextKeys.includes(item.key) && design.includes(`[[${item.key}]]`))) {
            throw new Error('A variable is still used in the saved builder design. Keep its name or change it in the email editor.');
        }
    }
    return config;
};

/** Present key → set; `null` → the key is removed (the editors treat absent as unset; `null` is never stored). */
const mergeConfig = (node: AutomationNode, patch: Record<string, unknown>): Record<string, any> => {
    const next: Record<string, any> = {...node.config};
    for (const [key, value] of Object.entries(patch)) {
        if (value === null) delete next[key];
        else next[key] = value;
    }
    return node.type === NodeType.CONTINUE_IF && 'conditionGroups' in patch
        ? writeContinueIfGroups(next, readConditionGroups({conditionGroups: patch.conditionGroups || []}))
        : next;
};

const assertLabel = (label: string) => {
    if (!label.trim() || /["\\\r\n]/.test(label) || label.length > 160) throw new Error('Use a short node name without quotes, backslashes or line breaks.');
};

// Labels are unique because references bind by label; Stops may share one (the API's DraftValidator rule).
const labelTaken = (node: AutomationNode, label: string, type: NodeType): boolean =>
    node.label === label && !(node.type === NodeType.FINISH && type === NodeType.FINISH);

const hasCycle = (nodes: AutomationNode[], edges: NodeConnection[]): boolean => {
    const visiting = new Set<string>();
    const visited = new Set<string>();
    const visit = (id: string): boolean => {
        if (visiting.has(id)) return true;
        if (visited.has(id)) return false;
        visiting.add(id);
        if (edges.some(edge => edge.sourceNodeId === id && visit(edge.targetNodeId))) return true;
        visiting.delete(id);
        visited.add(id);
        return false;
    };
    return nodes.some(node => visit(node.id));
};

const hasReferenceTo = (value: unknown, label: string): boolean => {
    if (typeof value === 'string') return value.includes(`{{"${label}".`) || value.includes(`{{\\"${label}\\".`);
    if (Array.isArray(value)) return value.some(entry => hasReferenceTo(entry, label));
    if (value && typeof value === 'object') return Object.values(value).some(entry => hasReferenceTo(entry, label));
    return false;
};

const referencesIn = (value: unknown): Array<{label: string; path: string}> => {
    if (typeof value === 'string') return Array.from(value.matchAll(/\{\{"([^"]+)"\.([^{}]+)\}\}/g), match => ({label: match[1], path: match[2]}));
    if (value && typeof value === 'object') return Object.values(value).flatMap(referencesIn);
    return [];
};

const invalidReferences = (nodes: AutomationNode[], connections: NodeConnection[]): Set<string> => {
    const invalid = new Set<string>();
    for (const node of nodes) {
        const upstream = new Set<string>();
        const visit = (target: string) => connections.filter(edge => edge.targetNodeId === target).forEach(edge => {
            if (!upstream.has(edge.sourceNodeId)) {upstream.add(edge.sourceNodeId); visit(edge.sourceNodeId);}
        });
        visit(node.id);
        const conditionFields = node.type === NodeType.CONTINUE_IF
            ? new Set(continueIfFields(nodes.filter(source => upstream.has(source.id)), undefined, [
                ...(nodes.some(source => source.type === NodeType.SEND_EMAIL) ? ['EMAIL'] : []),
                ...(nodes.some(source => source.type === NodeType.SEND_PUSH) ? ['PUSH'] : []),
            ]).map(field => field.value)) : new Set<string>();
        for (const reference of referencesIn(node.config)) {
            const sources = nodes.filter(source => (source.label || getNodeDisplay(source.type).label) === reference.label);
            const source = sources[0];
            const knownOutput = conditionFields.has(`{{"${reference.label}".${reference.path}}}`) ||
                (source && getNodeOutputs(source.type).some(output => output.name === reference.path
                    || (['object', 'array'].includes(output.type) && (reference.path.startsWith(`${output.name}.`) || reference.path.startsWith(`${output.name}[`)))));
            if (sources.length !== 1 || !upstream.has(source.id) || !knownOutput) invalid.add(`${node.id}: ${reference.label} · ${reference.path}`);
        }
    }
    return invalid;
};

/** Pure, atomic draft transaction. Nothing reaches React state until every operation has passed. */
export const applyAgentOperations = (draft: AgentDraft, operations: AgentOperation[], makeId = () => crypto.randomUUID()) => {
    if (!Array.isArray(operations) || operations.length > 100) throw new Error('The agent returned too many changes. Try a smaller request.');
    let nodes = draft.nodes.map(node => ({...node, config: {...node.config}, position: {...node.position}}));
    let connections = draft.connections.map(edge => ({...edge}));
    const changed = new Set<string>();
    const summary: string[] = [];
    const ids = new Map<string, string>();
    const existingIds = new Set(nodes.map(node => node.id));
    for (const op of operations.filter(op => op.kind === 'add')) {
        if (!op.nodeId || existingIds.has(op.nodeId) || ids.has(op.nodeId)) throw new Error('New steps need distinct temporary IDs.');
        ids.set(op.nodeId, makeId());
    }
    const id = (value?: string) => ids.get(value || '') || value || '';
    const removed: AutomationNode[] = [];

    for (const op of operations) {
        const nodeId = id(op.nodeId);
        if (op.kind === 'add') {
            if (!isKnownNodeType(op.nodeType as NodeType)) throw new Error(`Unsupported node type: ${op.nodeType}`);
            const label = op.label?.trim() || '';
            assertLabel(label);
            const type = op.nodeType as NodeType;
            if (nodes.some(node => labelTaken(node, label, type))) throw new Error(`A step named “${label}” already exists. Use a distinct name.`);
            // Placement is cosmetic: an unknown `afterNodeId` falls back silently; the connect operations catch a bad id.
            const anchor = (op.afterNodeId && nodes.find(node => node.id === id(op.afterNodeId)))
                || nodes.find(node => draft.selectedNodeIds.includes(node.id));
            const preferred = anchor
                ? {x: anchor.position.x, y: anchor.position.y + LAYOUT_ROW_SPACING}
                : {x: 0, y: nodes.length * LAYOUT_ROW_SPACING};
            const node: AutomationNode = {id: nodeId, type, label, config: getDefaultNodeConfig(type),
                position: findOpenPosition(preferred, nodes.map(entry => ({...entry, data: entry})))};
            node.config = remapAttachmentSourceNodeIds(mergeConfig(node, parseConfig(op, node)), ids);
            nodes.push(node);
            changed.add(node.id);
            summary.push(`Added ${label}`);
        } else if (op.kind === 'update') {
            const node = nodes.find(entry => entry.id === nodeId);
            if (!node) throw new Error('A step to update no longer exists.');
            const label = op.label?.trim() || node.label || node.type;
            assertLabel(label);
            if (label !== node.label) {
                if (nodes.some(entry => entry.id !== node.id && labelTaken(entry, label, node.type))) throw new Error(`A step named “${label}” already exists.`);
                if (node.label && nodes.some(entry => hasReferenceTo(entry.config, node.label!))) {
                    throw new Error(`“${node.label}” supplies variables. Keep its name for now; renaming referenced steps is not supported by this prototype.`);
                }
            }
            const config = remapAttachmentSourceNodeIds(mergeConfig(node, parseConfig(op, node)), ids);
            nodes = nodes.map(entry => entry.id === nodeId ? {...entry, label, config} : entry);
            changed.add(nodeId);
            summary.push(`Updated ${label}`);
        } else if (op.kind === 'remove') {
            const node = nodes.find(entry => entry.id === nodeId);
            if (!node) throw new Error('A step to remove no longer exists.');
            removed.push(node);
            nodes = nodes.filter(entry => entry.id !== nodeId);
            connections = connections.filter(edge => edge.sourceNodeId !== nodeId && edge.targetNodeId !== nodeId);
            summary.push(`Removed ${node.label || node.type}`);
        } else if (op.kind === 'connect' || op.kind === 'disconnect') {
            const edge: NodeConnection = {sourceNodeId: id(op.source), targetNodeId: id(op.target), sourceHandle: op.branch || undefined};
            const source = nodes.find(node => node.id === edge.sourceNodeId);
            const target = nodes.find(node => node.id === edge.targetNodeId);
            if (!source || !target) throw new Error('A connection refers to a missing step.');
            if (op.kind === 'disconnect') {
                if (!connections.some(entry => connectionKey(entry) === connectionKey(edge))) throw new Error('The connection to remove no longer exists.');
                connections = connections.filter(entry => connectionKey(entry) !== connectionKey(edge));
            } else {
                if (source.id === target.id || isTriggerNode(target.type)) throw new Error('This connection cannot lead into a trigger or back into the same step.');
                if (source.type === NodeType.IF_CONDITION ? !['true', 'false'].includes(op.branch || '') : !!op.branch) {
                    throw new Error('If connections must specify the Yes or No branch; other steps have a single output.');
                }
                if (!connections.some(entry => connectionKey(entry) === connectionKey(edge))) connections.push(edge);
            }
            changed.add(source.id);
            changed.add(target.id);
            summary.push(`${op.kind === 'connect' ? 'Connected' : 'Disconnected'} ${source.label || source.type} → ${target.label || target.type}`);
        } else {
            throw new Error('The agent returned an unsupported edit.');
        }
    }
    if (nodes.length > 100) throw new Error('Keep this prototype to 100 steps or fewer.');
    if (hasCycle(nodes, connections)) throw new Error('The proposed flow contains a cycle.');
    for (const node of removed) {
        if (node.label && nodes.some(entry => hasReferenceTo(entry.config, node.label!))) throw new Error(`Another step still uses variables from “${node.label}”.`);
    }
    const existingAttachmentProblems = new Set(draft.nodes.filter(node => teamEmailWithInvalidAttachments(node, draft.nodes, draft.connections)).map(node => node.id));
    for (const node of nodes) {
        if (teamEmailWithInvalidAttachments(node, nodes, connections) && !existingAttachmentProblems.has(node.id)) {
            throw new Error(`“${node.label || node.type}” selects an attachment step that does not run before it on every path.`);
        }
    }
    const existingInvalid = invalidReferences(draft.nodes, draft.connections);
    for (const reference of Array.from(invalidReferences(nodes, connections))) {
        if (!existingInvalid.has(reference)) throw new Error(`A variable has no matching earlier step or output: ${reference}.`);
    }
    return {nodes, connections, changedNodeIds: Array.from(changed).filter(nodeId => nodes.some(node => node.id === nodeId)), summary};
};
